<template>
  <BaseSelect
    ref="selectRef"
    v-bind="$attrs"
    :options="formattedOptions"
    :model-value="modelValue"
    :placeholder="placeholder"
    :searchable="true"
    :clearable="clearable"
    :with-button="withButton"
    :button-label="buttonLabel"
    :no-results-text="noResultsText"
    :external-search="true"
    :editable="editable"
    :edit-title="editTitle"
    @update:model-value="handleUpdate"
    @change="handleChange"
    @button-click="$emit('button-click')"
    @edit-option="openEditModal"
    @search="handleSearch"
    @clear-search="handleClearSearch"
    @dropdown-scroll-end="handleScrollEnd"
  />

  <AddDriverModal
    v-if="isEditModalVisible"
    :driver-id="editingDriverId"
    @driver-updated="handleDriverUpdated"
    @close="closeEditModal"
  />
</template>

<script>
import { mapState } from "pinia";

import { useDriverStore } from "@/stores/driverStore";
import { getCompanyId } from "@/utils/userUtils";
import driversApi from "@/services/api/driversApi";
import AddDriverModal from "@/components/Driver/AddDriverModal.vue";

export default {
  name: "DriverSelect",

  inheritAttrs: false,

  components: {
    AddDriverModal,
  },

  props: {
    modelValue: {
      type: [String, Number],
      default: null,
    },
    placeholder: {
      type: String,
      default: "Выберите водителя",
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    withButton: {
      type: Boolean,
      default: false,
    },
    buttonLabel: {
      type: String,
      default: "Добавить нового водителя",
    },
    noResultsText: {
      type: String,
      default: "Водители не найдены",
    },
    excludeIds: {
      type: Array,
      default: () => [],
    },
    editable: {
      type: Boolean,
      default: true,
    },
    // Прокидывается в BaseSelect для кнопки редактирования выбранного водителя
    editTitle: {
      type: String,
      default: "Редактировать водителя",
    },
  },

  emits: [
    "update:modelValue",
    "change",
    "button-click",
    "edit-option",
    "driver-updated",
  ],

  data() {
    return {
      companyId: getCompanyId(),
      preselectedOption: null,
      isSearchActive: false,
      isEditModalVisible: false,
      editingDriverId: null,
    };
  },

  computed: {
    ...mapState(useDriverStore, [
      "drivers",
      "pagination",
      "dataIsLoading",
    ]),

    formattedOptions() {
      const options = this.drivers
        .filter((driver) => {
          if (this.modelValue && driver.id === this.modelValue)
            return true;
          return !this.excludeIds.includes(driver.id);
        })
        .map((driver) => ({
          value: driver.id,
          label: driver.selector_name,
        }));

      if (
        this.preselectedOption &&
        !this.isSearchActive &&
        !options.find(
          (o) =>
            String(o.value) === String(this.preselectedOption.value)
        )
      ) {
        return [this.preselectedOption, ...options];
      }

      return options;
    },
  },

  watch: {
    async modelValue(newVal, oldVal) {
      if (!newVal) {
        this.preselectedOption = null;
        return;
      }

      if (newVal === oldVal) return;

      if (!this.isSearchActive && !this.preselectedOption) {
        await this.fetchPreselectedOption();
      }
    },
  },

  methods: {
    handleUpdate(value) {
      if (!value) {
        this.preselectedOption = null;
      }

      this.$emit("update:modelValue", value);
    },

    handleChange(option) {
      if (option) {
        this.preselectedOption = {
          value: option.value,
          label: option.label,
        };
      } else {
        this.preselectedOption = null;
      }

      this.$emit("change", option ? option.value : null);
    },

    async handleSearch(searchData) {
      this.isSearchActive = true;
      const store = useDriverStore();
      await store.fetchDrivers({ search: searchData.query });
    },

    async handleClearSearch() {
      this.isSearchActive = false;
      const store = useDriverStore();
      await store.fetchDrivers();
    },

    async handleScrollEnd() {
      const store = useDriverStore();
      if (
        store.pagination.page < store.pagination.pages &&
        !store.dataIsLoading
      ) {
        await store.loadMoreDrivers();
      }
    },

    openEditModal(driverId) {
      if (!driverId) return;

      this.editingDriverId = driverId;
      this.isEditModalVisible = true;
      this.$emit("edit-option", driverId);
    },

    closeEditModal() {
      this.isEditModalVisible = false;
      this.editingDriverId = null;
    },

    handleDriverUpdated(driver) {
      const store = useDriverStore();
      store.replaceDriver(driver);

      if (String(driver.id) === String(this.modelValue)) {
        this.preselectedOption = {
          value: driver.id,
          label: driver.selector_name,
        };
      }

      this.$nextTick(() => {
        this.$refs.selectRef?.reselectSelectedOption();
      });
      this.$emit("driver-updated", driver);
    },

    async fetchPreselectedOption() {
      if (!this.modelValue) return;

      const store = useDriverStore();
      const exists = store.drivers.find(
        (d) => String(d.id) === String(this.modelValue)
      );
      if (exists) return;

      try {
        const response = await driversApi.show(
          this.companyId,
          this.modelValue
        );
        const driver = response.data.result;

        this.preselectedOption = {
          value: driver.id,
          label: driver.selector_name,
        };
      } catch (error) {
        console.error(
          "Ошибка при загрузке выбранного водителя:",
          error
        );
      }
    },
  },

  async mounted() {
    const store = useDriverStore();
    await store.fetchDrivers();
    await this.fetchPreselectedOption();
  },
};
</script>
